import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense.ts";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@babackapp/ui/components/dialog";
import { useBoolean } from "@/hooks/use-boolean.ts";
import useAddReturnReason from "@babackapp/private-api-client/client/use-add-return-reason";
import { PropsWithChildren, useCallback, useMemo, useState } from "react";
import ReturnReasonForm from "./return-reason-form.tsx";

import { z } from "zod";
import useTranslation from "@babackapp/translations/use-translation";

const babackStandardReason = z.enum([
  "COLOR",
  "DEFECTIVE",
  "NOT_AS_DESCRIBED",
  "OTHER",
  "STYLE",
  "SIZE_TOO_LARGE",
  "SIZE_TOO_SMALL",
  "UNKNOWN",
  "UNWANTED",
  "WRONG_ITEM",
]);

export type BabackStandardReason = z.infer<typeof babackStandardReason>;

const returnReasonFormSchema = z.object({
  babackStandardReason,
  babackStandardReasonNote: z.string().optional(),
  images: z.enum(["REQUIRED", "OPTIONAL", "NOT_AVAILABLE"]),
  comments: z.enum(["REQUIRED", "OPTIONAL", "NOT_AVAILABLE"]),
  externalId: z.string().optional(),

  approvalRequired: z.boolean(),
  skipReturnCost: z.boolean(),
  skipReshipping: z.boolean(),
  highlighted: z.boolean(),
  reservedForLoggedUser: z.boolean(),

  values: z.array(
    z.object({
      lang: z.string(),
      text: z.string(),
    }),
  ),
});

export type ReturnReasonFormValues = z.infer<typeof returnReasonFormSchema>;

export default function AddReturnReasonDialog({ children }: PropsWithChildren) {
  const t = useTranslation();
  const [error, setError] = useState<{ title?: string; error?: string } | null>(
    null,
  );
  const open = useBoolean(false);
  const { data: shop } = useCurrentShopSuspense();
  const { data: languages } = useCurrentShopLocalesAvailables();
  const { mutateAsync: addReturnReason } = useAddReturnReason(shop);

  const defaultValuesFields = useMemo(() => {
    if (!languages) return null;
    return languages.map((lang) => ({ lang, text: "" }));
  }, [languages]);

  const onSubmit = useCallback(
    async (value: ReturnReasonFormValues) => {
      await addReturnReason({
        active: true,
        babackStandardReason: value.babackStandardReason,
        // TODO: Type should be string | undefined in the API
        babackStandardReasonNote:
          value.babackStandardReason === "OTHER"
            ? value.babackStandardReasonNote || undefined
            : undefined,
        externalId: value.externalId || undefined,
        values: value.values.map((v) => ({ lang: v.lang, text: v.text })),
        rules: {
          approvalRequired: { available: value.approvalRequired },
          comment: {
            available: ["REQUIRED", "OPTIONAL"].includes(value.comments),
            required: value.comments === "REQUIRED",
          },
          photoRequired: {
            available: ["REQUIRED", "OPTIONAL"].includes(value.images),
            required: value.images === "REQUIRED",
          },
          skipReturnCost: { available: value.skipReturnCost },
          skipReshipping: { available: value.skipReshipping },
          highlighted: { available: value.highlighted },
          reservedForLoggedUser: { available: value.reservedForLoggedUser },
        },
      });
      open.setFalse();
    },
    [addReturnReason, open],
  );

  if (!shop || !defaultValuesFields) return null;

  return (
    <Dialog open={open.value} onOpenChange={open.setValue}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="pt-10" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{t("settings.reasons.popup.titleOnAdd")}</DialogTitle>
        </DialogHeader>
        <ReturnReasonForm
          onSubmit={onSubmit}
          defaultValues={{ values: defaultValuesFields }}
          setError={setError}
          error={error}
        />
      </DialogContent>
    </Dialog>
  );
}

/**
 * Strangely, importing this hook doesnt work, so I copy it here.
 * TODO:  Fix this. I think its a bug with Meteor..
 */
export function useCurrentShopLocalesAvailables() {
  const { data: shop } = useCurrentShopSuspense();

  return {
    data: shop?.preferences.languages,
  };
}
